import React from 'react';
import { Link } from 'react-router-dom';
import { FiPlay, FiHeart } from 'react-icons/fi';
import { TrailerWithMovie } from '../utils/sanityClient';
import {
  getYouTubeThumbnailUrl,
  getYouTubeThumbnailFallbackUrl,
  isYouTubeUrl,
} from '../utils/videoUtils';
import { useFavorites } from '../hooks/useFavorites';
import { useAuth } from '../contexts/AuthContext';

interface TrailerCardProps {
  trailer: TrailerWithMovie;
  onPlayClick: (videoUrl: string) => void;
}

const TrailerCard: React.FC<TrailerCardProps> = ({ trailer, onPlayClick }) => {
  const { title, videoUrl, isMainTrailer, movie } = trailer;
  const isYouTube = isYouTubeUrl(videoUrl);
  const { user } = useAuth();
  const { toggleFavorite, isFavorite } = useFavorites();

  const handlePlayClick = (e: React.MouseEvent) => {
    if (!isYouTube) return;
    // On desktop, prevent default and open modal; on mobile, let default link behavior open YouTube
    if (window.innerWidth >= 768) {
      e.preventDefault();
      onPlayClick(videoUrl);
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (user && movie) {
      toggleFavorite(movie._id);
    }
  };

  return (
    <div className="card group w-full flex-shrink-0 overflow-hidden">
      {/* Thumbnail */}
      <div className="relative overflow-hidden rounded-t-lg">
        {isYouTube ? (
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block relative"
            onClick={handlePlayClick}
          >
            <img
              src={getYouTubeThumbnailUrl(videoUrl)!}
              alt={`${title} thumbnail`}
              className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
              onLoad={(e) => {
                // YouTube returns a 120x90 grey placeholder (HTTP 200) when
                // maxresdefault doesn't exist — detect it by naturalWidth
                if (e.currentTarget.naturalWidth <= 120) {
                  const fallbackUrl = getYouTubeThumbnailFallbackUrl(videoUrl);
                  if (fallbackUrl) {
                    e.currentTarget.src = fallbackUrl;
                  }
                }
              }}
              onError={(e) => {
                const fallbackUrl = getYouTubeThumbnailFallbackUrl(videoUrl);
                if (fallbackUrl && e.currentTarget.src !== fallbackUrl) {
                  e.currentTarget.src = fallbackUrl;
                }
              }}
            />
            {/* Play overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/60 transition-colors duration-300">
              <div className="w-12 h-12 rounded-full bg-accent-teal/90 flex items-center justify-center shadow-lg">
                <FiPlay className="w-6 h-6 text-white translate-x-0.5" />
              </div>
            </div>
            {/* Main trailer badge */}
            {isMainTrailer && (
              <div className="absolute bottom-2 left-2 bg-accent-orange text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                MAIN TRAILER
              </div>
            )}
          </a>
        ) : (
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block relative aspect-video bg-primary/10 flex items-center justify-center"
          >
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <FiPlay className="w-6 h-6 text-primary" />
            </div>
            {isMainTrailer && (
              <div className="absolute bottom-2 left-2 bg-accent-orange text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                MAIN TRAILER
              </div>
            )}
          </a>
        )}

        {/* Favorite button — only shown when user is logged in and trailer has a linked movie */}
        {user && movie && (
          <button
            onClick={handleFavoriteClick}
            className="absolute top-2 left-2 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75 hover:text-red-400 transition-colors z-10"
            aria-label={isFavorite(movie._id) ? 'Remove from favorites' : 'Add to favorites'}
          >
            <FiHeart
              className={`w-4 h-4 ${isFavorite(movie._id) ? 'fill-red-500 text-red-500' : 'text-white'}`}
            />
          </button>
        )}
      </div>

      {/* Card Body */}
      <div className="p-3 bg-white rounded-b-lg">
        {/* Movie title - links to movie detail */}
        {movie ? (
          <Link
            to={`/movie/${movie.slug}`}
            className="block font-heading font-bold text-md mb-1 truncate text-secondary-dark hover:text-primary transition-colors duration-200"
            title={movie.title}
          >
            {movie.title}
          </Link>
        ) : (
          <h3
            className="font-heading font-bold text-md mb-1 truncate text-secondary-dark"
            title={title}
          >
            {title}
          </h3>
        )}

        {/* Trailer label */}
        <p className="text-xs text-secondary-light mb-3 truncate">{title}</p>

        {/* Watch button - opens YouTube directly (no modal) */}
        <a
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary text-sm w-full text-center flex items-center justify-center gap-1"
        >
          Watch Trailer
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default TrailerCard;
